import { LivekitBaseController } from "./livekit_base_controller"
import { Track } from "livekit-client"

export default class extends LivekitBaseController {
  static targets = [
    "button", "stopButton", "statusText", "transcript", "transcriptWrapper",
    "interrupt", "orbWrapper", "orb", "glow",
    "ripple1", "ripple2", "ripple3",
    "orbIconIdle", "orbIconListening", "orbIconThinking", "orbIconSpeaking",
    "helpText"
  ]
  static values = {
    conversationId: Number,
    userSgid: String,
    authorGender: String,
    language: String,
    livekitWsUrl: String,
    isNewConversation: Boolean
  }

  connect() {
    this.isActive = false
    this.room = null
    this.userTranscript = ""
    this.aiTranscript = ""
    this.partialText = ""
    this.aiAudioElement = null
    this.isAISpeaking = false
    this.lastBargeInTime = 0
    this.welcomeSpoken = false
    this.bargeInAudioContext = null
    this.currentState = "idle"
  }

  disconnect() {
    this.stopVoiceMode()
  }

  async toggleVoiceMode() {
    if (this.isActive) {
      this.handleStopVoiceMode()
    } else {
      await this.handleStartVoiceMode()
    }
  }

  async interruptAI() {
    if (!this.room || !this.isActive) return

    const encoder = new TextEncoder()
    const data = encoder.encode(JSON.stringify({ type: "barge_in" }))
    await this.room.localParticipant.publishData(data, { reliable: true, topic: "control" })
  }

  async handleStartVoiceMode() {
    try {
      this.updateUIState("connecting")

      const metadata = {
        mode: "voice",
        tutor_conversation_id: this.conversationIdValue,
        user_sgid: this.userSgidValue,
        coach_gender: this.authorGenderValue || "female",
        language: this.languageValue || "en",
        welcome_message: this.isNewConversationValue && !this.welcomeSpoken
      }

      const tokenData = await this.fetchLiveKitToken(metadata)

      await this.connectToRoom(tokenData, {
        onTrackSubscribed: this.handleTrackSubscribed.bind(this),
        onDataReceived: this.handleDataReceived.bind(this)
      })

      this.isActive = true

      await this.publishMicrophone()

      if (metadata.welcome_message) {
        this.updateUIState("speaking")
        this.welcomeSpoken = true
      } else {
        this.updateUIState("listening")
      }
    } catch (error) {
      this.handleVoiceError(error)
    }
  }

  handleStopVoiceMode() {
    if (!this.isActive) return

    if (this.room) {
      try {
        const encoder = new TextEncoder()
        const data = encoder.encode(JSON.stringify({ type: "client_stopping" }))
        this.room.localParticipant.publishData(data, { reliable: true, topic: "control" })
      } catch (error) {
        console.error('[TutorVoice] Failed to send stop signal:', error)
      }
    }

    this.stopPublishing()
    this.disconnectFromRoom()
    this.setActiveState(false)
  }

  handleTrackSubscribed(track, publication, participant) {
    if (track.kind === Track.Kind.Audio && participant.identity?.includes('agent')) {
      const audioElement = track.attach()
      audioElement.autoplay = true
      audioElement.volume = 1.0
      document.body.appendChild(audioElement)
      this.aiAudioElement = audioElement
    }
  }

  handleDataReceived(payload, participant, kind, topic) {
    if (topic !== "voice") return

    try {
      const text = new TextDecoder().decode(payload)
      const msg = JSON.parse(text)

      if (msg.type === "state") {
        this.isAISpeaking = (msg.state === "speaking")
      }

      this.handleVoiceMessage(msg)
    } catch (e) {
      console.error("[TutorVoice] Bad payload:", e)
    }
  }

  async publishMicrophone() {
    try {
      await this.room.localParticipant.setMicrophoneEnabled(true, {
        echoCancellation: { exact: true },
        noiseSuppression: { exact: true },
        autoGainControl: true,
        sampleRate: 48000,
        channelCount: 1,
      })

      await new Promise(resolve => setTimeout(resolve, 200))

      const trackPublications = this.room.localParticipant.trackPublications
      if (trackPublications && trackPublications.size > 0) {
        for (const [, publication] of trackPublications) {
          if (publication.kind === Track.Kind.Audio && publication.track) {
            this.setupClientSideBargeInDetection(publication.track)
            break
          }
        }
      }
    } catch (error) {
      console.error('[TutorVoice] Failed to publish microphone:', error)
      throw error
    }
  }

  disconnectFromRoom() {
    if (this.bargeInAudioContext) {
      this.bargeInAudioContext.close()
      this.bargeInAudioContext = null
    }
    if (this.aiAudioElement) {
      this.aiAudioElement.remove()
      this.aiAudioElement = null
    }
    super.disconnectFromRoom()
  }

  handleVoiceMessage(msg) {
    const { type, text, state } = msg

    switch (type) {
      case "state":
        this.updateUIState(state)
        break
      case "partial":
        this.partialText = text || ""
        this.renderTranscript()
        break
      case "final":
        this.userTranscript += (this.userTranscript ? " " : "") + (text || "")
        this.partialText = ""
        this.renderTranscript()
        break
      case "done":
        break
    }
  }

  renderTranscript() {
    if (!this.hasTranscriptTarget) return

    const userText = this.userTranscript + (this.partialText ? ` ${this.partialText}` : "")

    let html = ""
    if (userText) {
      html += `<div class="mb-4">
        <div class="flex items-center gap-2 mb-1.5">
          <div class="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center">
            <div class="w-2 h-2 rounded-full bg-indigo-500"></div>
          </div>
          <span class="text-indigo-600 text-xs font-medium uppercase tracking-wider">You</span>
        </div>
        <div class="text-gray-700 text-sm leading-relaxed pl-7">${this.escapeHtml(userText)}</div>
      </div>`
    }
    if (this.aiTranscript) {
      html += `<div class="mb-4">
        <div class="flex items-center gap-2 mb-1.5">
          <div class="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
            <div class="w-2 h-2 rounded-full bg-emerald-500"></div>
          </div>
          <span class="text-emerald-600 text-xs font-medium uppercase tracking-wider">AI Tutor</span>
        </div>
        <div class="text-gray-700 text-sm leading-relaxed pl-7">${this.escapeHtml(this.aiTranscript)}</div>
      </div>`
    }

    this.transcriptTarget.innerHTML = html || '<div class="text-gray-400 text-sm text-center py-4">Your conversation will appear here...</div>'

    if (this.hasTranscriptWrapperTarget) {
      this.transcriptWrapperTarget.scrollTop = this.transcriptWrapperTarget.scrollHeight
    }
  }

  setActiveState(isActive) {
    this.isActive = isActive
    this.updateUIState(isActive ? "listening" : "idle")
  }

  updateUIState(state) {
    this.applyStateClass(state)
    this.updateOrbIcon(state)

    const statusMessages = {
      'idle': null,
      'connecting': "Connecting...",
      'listening': "Listening",
      'thinking': "Processing...",
      'speaking': "Speaking",
      'interrupted': "Listening",
      'disconnected': null
    }

    const uiStates = {
      'idle': () => {
        this.showElement(this.buttonTarget)
        this.hideElement(this.stopButtonTarget)
        this.hideElement(this.interruptTarget)
        this.setStatusText(statusMessages['idle'])
        this.showHelpText(true)
      },
      'connecting': () => {
        this.hideElement(this.buttonTarget)
        this.showElement(this.stopButtonTarget)
        this.hideElement(this.interruptTarget)
        this.setStatusText(statusMessages['connecting'])
        this.showHelpText(false)
      },
      'listening': () => {
        this.hideElement(this.buttonTarget)
        this.showElement(this.stopButtonTarget)
        this.hideElement(this.interruptTarget)
        this.setStatusText(statusMessages['listening'])
        this.showHelpText(false)
      },
      'thinking': () => {
        this.hideElement(this.buttonTarget)
        this.showElement(this.stopButtonTarget)
        this.hideElement(this.interruptTarget)
        this.setStatusText(statusMessages['thinking'])
        this.showHelpText(false)
      },
      'speaking': () => {
        this.hideElement(this.buttonTarget)
        this.showElement(this.stopButtonTarget)
        this.showInterruptButton()
        this.setStatusText(statusMessages['speaking'])
        this.showHelpText(false)
      },
      'interrupted': () => {
        this.hideElement(this.interruptTarget)
        this.setStatusText(statusMessages['interrupted'])
        this.showHelpText(false)
      },
      'disconnected': () => {
        this.showElement(this.buttonTarget)
        this.hideElement(this.stopButtonTarget)
        this.hideElement(this.interruptTarget)
        this.setStatusText(statusMessages['disconnected'])
        this.showHelpText(true)
      }
    }

    const handler = uiStates[state]
    if (handler) handler()
  }

  applyStateClass(state) {
    const panel = this.element
    const states = ['idle', 'connecting', 'listening', 'thinking', 'speaking', 'interrupted', 'disconnected']
    states.forEach(s => panel.classList.remove(`voice-state-${s}`))
    panel.classList.add(`voice-state-${state}`)
    this.currentState = state
  }

  updateOrbIcon(state) {
    const icons = {
      idle: 'orbIconIdle',
      connecting: 'orbIconIdle',
      listening: 'orbIconListening',
      thinking: 'orbIconThinking',
      speaking: 'orbIconSpeaking',
      interrupted: 'orbIconListening',
      disconnected: 'orbIconIdle'
    }

    const allIcons = ['orbIconIdle', 'orbIconListening', 'orbIconThinking', 'orbIconSpeaking']
    allIcons.forEach(name => {
      const target = `has${name.charAt(0).toUpperCase() + name.slice(1)}Target`
      if (this[target]) {
        this[`${name}Target`].classList.add('hidden')
      }
    })

    const activeIcon = icons[state] || 'orbIconIdle'
    const activeTarget = `has${activeIcon.charAt(0).toUpperCase() + activeIcon.slice(1)}Target`
    if (this[activeTarget]) {
      this[`${activeIcon}Target`].classList.remove('hidden')
    }
  }

  setStatusText(message) {
    if (!this.hasStatusTextTarget) return
    this.statusTextTarget.textContent = message || ""
  }

  showHelpText(visible) {
    if (!this.hasHelpTextTarget) return
    this.helpTextTarget.style.opacity = visible ? "1" : "0"
  }

  showElement(target) {
    if (!target) return
    target.classList.remove('hidden')
    target.style.removeProperty('display')
  }

  hideElement(target) {
    if (!target) return
    target.style.removeProperty('display')
    target.classList.add('hidden')
  }

  showInterruptButton() {
    if (this.hasInterruptTarget) {
      this.interruptTarget.classList.remove('hidden')
    }
  }

  handleVoiceError(error) {
    console.error("Tutor voice mode error:", error)
    this.setStatusText(`Connection failed — please try again`)
    this.setActiveState(false)
  }

  setupClientSideBargeInDetection(track) {
    if (!track || !track.mediaStreamTrack) return

    if (this.bargeInAudioContext) {
      this.bargeInAudioContext.close()
    }

    try {
      const audioContext = new AudioContext()
      this.bargeInAudioContext = audioContext
      const mediaStream = new MediaStream([track.mediaStreamTrack])
      const source = audioContext.createMediaStreamSource(mediaStream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 2048
      source.connect(analyser)

      const bufferLength = analyser.frequencyBinCount
      const dataArray = new Uint8Array(bufferLength)
      const SPEECH_THRESHOLD = 20

      const checkAudioLevel = () => {
        if (!this.isActive) return

        analyser.getByteFrequencyData(dataArray)
        const average = dataArray.reduce((a, b) => a + b) / bufferLength

        if (this.isAISpeaking && average > SPEECH_THRESHOLD) {
          const now = Date.now()
          if (now - this.lastBargeInTime > 500) {
            this.lastBargeInTime = now
            this.interruptAI()
          }
        }

        requestAnimationFrame(checkAudioLevel)
      }

      checkAudioLevel()
    } catch (error) {
      console.error('[TutorVoice] Failed to setup barge-in detection:', error)
    }
  }

  generateRoomName() {
    return `tutor-voice-${this.conversationIdValue}-${Date.now()}`
  }

  generateParticipantName() {
    return `user-${this.conversationIdValue}`
  }

  startVoiceMode() {
    return this.handleStartVoiceMode()
  }

  stopVoiceMode() {
    return this.handleStopVoiceMode()
  }
}
